/**
 * THE TWO MARKS THE DM SURFACE DRAWS — OSO-549 / OSO-550
 *
 * A conversation can be behind in two different ways, and they are not the
 * same fact:
 *
 *   UNREAD MESSAGES — `unreadCount`. A number, and the server counts only real
 *     messages for it (`kind = 'regular'`), so "Niels forlod samtalen" has
 *     never inflated it and still does not.
 *
 *   UNSEEN ACTIVITY — `hasUnseenActivity`. Somebody renamed the thread, or
 *     reacted to a message you sent. Neither is a message, so neither has
 *     anything to count; what they need is "open this".
 *
 * Two facts, two marks: a FILLED dot for messages you have not read, a HOLLOW
 * ring for something that happened. The ring is deliberately the same colour
 * and the same size — it is the quieter member of one family, not a second
 * alarm — and it is only ever drawn when the filled one is not, because a
 * thread with unread messages is already saying the louder thing.
 *
 * NEITHER OF THESE IS THE BELL, for the reason recorded on the server at
 * `signalConversationActivity` (backend/src/data/dms.ts).
 *
 * Both components exist because the same markup was already copied across the
 * inbox row, the header popup's rows and the FOUR header badges (desktop icon,
 * mobile icon, the profile menu and the mobile nav). Adding a second state to
 * six hand-rolled copies is how the six start disagreeing.
 */
import { CountBadge, Dot, Ring } from "@/components/UnreadMarker";

/**
 * The per-conversation mark, on an inbox row.
 *
 * `unread` wins when both are true: the row is already drawing attention, and
 * two marks side by side would ask the reader to tell apart a distinction that
 * costs them nothing.
 */
export function ConversationMark({
  unread,
  activity,
  className,
}: {
  unread: boolean;
  activity: boolean;
  className?: string;
}) {
  if (unread) return <Dot label="Ulæste beskeder" className={className} />;
  if (!activity) return null;
  return <Ring label="Nyt i samtalen" className={className} />;
}

/**
 * The header badge on a messages icon or nav item.
 *
 * The NUMBER IS CONVERSATIONS, not messages: `useUnreadMessageCount` reads
 * `badges.unreadConversationCount`, so three unread messages in one thread
 * render "1". Muted conversations are excluded by the server, so it is always
 * a number the reader can clear. (The name of the hook is older than the
 * count it returns; changing which one it reads would move the app badge and
 * the service worker with it, and that is not this component's call.)
 *
 * When there is nothing to count but something has still happened, the same
 * corner carries a bare dot instead: there is no number that would be honest
 * for "somebody reacted to your message".
 */
export function DmBadge({
  count,
  activity,
  className,
}: {
  count: number;
  activity: boolean;
  className?: string;
}) {
  if (count > 0) {
    return <CountBadge count={count} label={`${count} ulæste samtaler`} className={className} />;
  }
  if (!activity) return null;
  return (
    <span
      role="status"
      aria-label="Nyt i dine beskeder"
      title="Nyt i dine beskeder"
      className={`w-2.5 h-2.5 bg-primary rounded-full${className ? ` ${className}` : ""}`}
    />
  );
}
